// src/encounter/structured-note/history-notes-line.tsx
//
// PROD-3043 · frame v2 only (design desk D-73 amendment, 6 Oct). "History notes": the HPI narrative typed
// inline, as a note line, under the complaint list in the HPI block.
//
// THE FIELD ALREADY EXISTS. It is the chief-complaint document's `comment` (a Quill Delta) + `notePlainText`,
// the pair Flutter's Chief Complaint "Add notes" writes (`common_encounter_component_base_tab.dart:448-470`,
// `CreateChiefCompaintEvent(comment:, notePlainText:)`). One comment per document, not per complaint, so
// one line however many complaints are recorded. The page writes it through the chief-complaint save it
// already has (the CreateChiefComplaint replace-all, `buildPatientComplaint`). The wire does not change.
//
// THE RULES ARE THE A&P LINE'S (ap-inline-narrative.tsx):
//   · lossless (`readInlineNarrative`): plain text plus phrase pieces is typed inline; a formatted comment
//     reads as text with "Edit", which opens today's "Add Text Notes" dialog;
//   · the draft is saved on a pause, on blur, on unmount, on page hide and at a freeze, and is never
//     written into another patient's or visit's record (inline-note-line.tsx);
//   · rule 38: the chief complaint is an encounter record, so the line is read-only after the provider
//     signature.
// One narrative line per block: when the template carries its own HPI narrative item, that item is the
// line and this one is not drawn (structured-note-section.tsx).
import { useState } from 'react';
import { Button, NoteLine, type AuthorRole } from '@scripsteam/scrips-signal-ds';
import { ApNoteDialog } from '../ap-note-dialog';
import { lineSegsToWire, otherRecord, readInlineNarrative, sameRecord } from '../lib/narrative-line-wire';
import type { NoteWhere } from '../lib/note-pieces';
import type { NoteMacroScope } from '../services/use-note-macros';
import { InlineNoteLine } from './inline-note-line';
import { lineEditors } from './note-phrases';
import type { StructuredNotePhrases } from './structured-note-section';

/** The line's label (D-73 amendment). */
export const HISTORY_NOTES_LABEL = 'History notes';
/** The DOM id of the field. */
export const HISTORY_NOTES_FIELD_ID = 'hpi-history-notes-line';

/** The HPI narrative as the page holds it (frame v2 only). */
export interface StructuredHistoryNote {
  /** The chief-complaint document's `comment` (a Quill Delta ops-array JSON string). */
  comment?: string | null;
  /** Its `notePlainText`. */
  notePlainText?: string | null;
  /** Write the pair through the chief-complaint save. Absent → read-only. */
  onChange?: (comment: string, notePlainText: string) => void;
  /** Whose document it is: a draft is never written into another record. */
  owner: NoteWhere;
  /** The chief-complaint document has loaded (the page's `ccReady`); until then nothing is written. */
  ready: boolean;
  /** The visit's specialty and type, for the dialog's phrase list (as the A&P note's). */
  macroScope?: NoteMacroScope;
}

export interface HistoryNotesLineProps {
  note: StructuredHistoryNote;
  author: AuthorRole;
  /** Rule 38: the provider has signed (records freeze, amending or not). */
  frozen: boolean;
  phrases?: StructuredNotePhrases;
}

/** The empty comment, as the chief-complaint note saves it (`note-delta.ts` serializeNote: '' / ''). */
function toWire(plain: string, delta: string): { comment: string; plain: string } {
  return plain.trim() ? { comment: delta, plain } : { comment: '', plain: '' };
}

export function HistoryNotesLine({ note, author, frozen, phrases }: HistoryNotesLineProps) {
  // The record the Edit dialog was opened for (null = closed). The dialog's draft is that record's: if the
  // line is re-rendered for another record it closes without writing, and Save never writes elsewhere
  // (cross-model review: the dialog had no owner binding of its own).
  const [openedFor, setOpenedFor] = useState<NoteWhere | null>(null);
  if (openedFor && otherRecord(openedFor, note.owner)) setOpenedFor(null);
  // Ids that resolve while the dialog is open are adopted, so a later switch is told apart (unknown → A → B).
  else if (openedFor && ((!openedFor.patientId && !!note.owner.patientId) || (!openedFor.encounterId && !!note.owner.encounterId)))
    setOpenedFor({ patientId: openedFor.patientId || note.owner.patientId, encounterId: openedFor.encounterId || note.owner.encounterId });
  const narrative = readInlineNarrative(note.comment, note.notePlainText, note.owner);
  const canWrite = !frozen && note.ready && !!note.onChange;
  const editable = canWrite && narrative.editable;
  const dialogEdit = canWrite && !narrative.editable;
  const editors = lineEditors('hpi', author, phrases?.discipline);
  const footnote =
    dialogEdit ? (
      <NoteLine label="" align="center">
        <Button variant="tertiary" size="sm" onClick={() => setOpenedFor({ ...note.owner })} aria-label={`Edit ${HISTORY_NOTES_LABEL.toLowerCase()}`}>
          Edit
        </Button>
      </NoteLine>
    ) : undefined;
  return (
    <div data-testid="hpi-history-notes" data-inline-editable={editable ? 'true' : 'false'}>
      <InlineNoteLine
        id={HISTORY_NOTES_FIELD_ID}
        label={HISTORY_NOTES_LABEL}
        segs={narrative.segs}
        // Kept while frozen: a draft typed just before the signature is handed over, never dropped (L7).
        onCommit={(next) => {
          const wire = lineSegsToWire(next);
          const out = toWire(wire.plain, wire.delta);
          note.onChange?.(out.comment, out.plain);
        }}
        readOnly={!editable}
        macros={phrases?.macros}
        editors={editors}
        author={author}
        pieces={phrases?.pieces}
        suggested={phrases?.suggestFor?.(editors)}
        chartReads={phrases?.chartReads}
        renderChartPiece={phrases?.renderChartPiece}
        owner={note.owner}
        // L10: empty, it draws nothing; the block's header offers "Add note".
        start="block"
        footnote={footnote}
      />
      {dialogEdit && (
        <ApNoteDialog
          open={!!openedFor}
          onClose={() => setOpenedFor(null)}
          deltaJson={note.comment ?? undefined}
          plainText={note.notePlainText ?? undefined}
          editorLabel={HISTORY_NOTES_LABEL}
          phraseEditors={editors}
          macroScope={note.macroScope}
          chartReads={phrases?.chartReads}
          onSave={(plain, delta) => {
            // The line points at a record whose ids are not known (yet): nothing is written, and the dialog
            // stays open with its text, so it can be saved once the visit is known again.
            const known = !!note.owner.patientId && !!note.owner.encounterId;
            if (!openedFor || !known || !sameRecord(openedFor, note.owner)) return;
            const out = toWire(plain.replace(/\n+$/, ''), delta);
            note.onChange?.(out.comment, out.plain);
            setOpenedFor(null);
          }}
        />
      )}
    </div>
  );
}
